import axios from "axios";
import { getErrorMessage } from "../utils/formatters";
import {
  createCircuitBreaker,
  createCircuitBreakerError,
  CIRCUIT_BREAKER_MESSAGES,
} from "../utils/circuitBreaker";
import { parseEnvInt } from "../utils/env";
import { clearInflightCache } from "./api";
import { STORAGE_KEYS } from "../config/constants";
import errorTracker from "../utils/errorTracker";

const API_URL = process.env.REACT_APP_API_URL || "/api";

// ============================================================================
// Circuit Breaker for Auth Service
// ============================================================================
// Separate circuit breaker from main API to avoid auth failures blocking
// all API calls and vice versa. Auth endpoints have different failure patterns.

const circuitBreakerConfig = {
  failureThreshold: parseEnvInt(
    process.env.REACT_APP_AUTH_CIRCUIT_BREAKER_THRESHOLD,
    5,
  ),
  resetTimeoutMs: parseEnvInt(
    process.env.REACT_APP_AUTH_CIRCUIT_BREAKER_TIMEOUT_MS,
    30000,
  ),
};

const authCircuitBreaker = createCircuitBreaker("Auth", circuitBreakerConfig);

/**
 * Separate axios instance for auth operations.
 * withCredentials: true sends the httpOnly auth cookies on every request so
 * the backend can authenticate without an explicit Authorization header.
 * This instance does NOT have the 401 interceptor that redirects to login,
 * which would cause redirect loops during login/register failures.
 */
const authClient = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor for circuit breaker
authClient.interceptors.request.use(
  (config) => {
    if (!authCircuitBreaker.check()) {
      return Promise.reject(
        createCircuitBreakerError(CIRCUIT_BREAKER_MESSAGES.AUTH_UNAVAILABLE),
      );
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Response interceptor: circuit-breaker health tracking + envelope unwrapping.
// Raw shape from the server: { status: "success", data: { ... } }
// Shape seen by callers:      { ... }   (the inner `data` field only)
// Spreads into a new object rather than mutating — makes the transform visible
// to any future logging interceptor added after this one.
authClient.interceptors.response.use(
  (response) => {
    authCircuitBreaker.recordSuccess();
    const body = response.data;
    if (body?.status === "success" && body.data !== undefined) {
      return { ...response, data: body.data };
    }
    return response;
  },
  (error) => {
    // Record failures for server errors, rate-limits, and network outages.
    // status is undefined when error.response is absent (network error), so
    // the numeric comparisons below evaluate to false — isNetworkError catches
    // that case explicitly rather than relying on undefined arithmetic.
    const status = error.response?.status;
    const isServerOrRateLimit =
      status !== undefined && (status >= 500 || status === 429);
    const isNetworkError = !error.response;
    if (isServerOrRateLimit || isNetworkError) {
      authCircuitBreaker.recordFailure();
    }
    if (process.env.NODE_ENV === "development") {
      console.error("Auth Error:", error.response?.data || error.message);
    } else if (isServerOrRateLimit || isNetworkError) {
      // Report server errors and network failures to telemetry in production.
      // Client errors (4xx) are expected in normal auth flow (wrong password,
      // expired token) and must not pollute the error tracker.
      errorTracker.captureException(error);
    }
    return Promise.reject(error);
  },
);

/**
 * Create a standardized auth error with consistent properties.
 * @param {Error} error - Original error from axios
 * @param {string} defaultMessage - Fallback message if error has none
 * @param {boolean} includeFieldErrors - Whether to include field-level errors
 * @returns {Error} Standardized auth error
 */
function createAuthError(error, defaultMessage, includeFieldErrors = false) {
  const message = getErrorMessage(error, defaultMessage);
  const authError = new Error(message);
  authError.response = error.response ?? null;
  authError.isNetworkError = !error.response;
  authError.isCircuitBreakerError = error.isCircuitBreakerError ?? false;
  if (includeFieldErrors) {
    authError.errors = error.response?.data?.errors ?? null;
  }
  return authError;
}

class AuthService {
  user = null;

  /**
   * Store user data in localStorage after a successful login or register.
   * Tokens are delivered as httpOnly cookies by the server and are never
   * readable by JavaScript — only user info is stored here.
   *
   * Throws when the server response omits the user object, because in that
   * case the server has already set auth cookies but isAuthenticated() will
   * return false — stranding the user in a redirect loop with no visible error.
   * Failing loudly here surfaces the server bug at the point of occurrence.
   * @private
   */
  _storeAuthData(data) {
    if (data == null) {
      throw new TypeError(
        "_storeAuthData: expected a server response object, got " +
          JSON.stringify(data),
      );
    }
    if (data.user == null) {
      throw new Error(
        "_storeAuthData: server auth response contained no user object — " +
          "this indicates a server-side bug (cookies are set but localStorage " +
          "would be empty, stranding the user in a redirect loop)",
      );
    }
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(data.user));
    this.user = data.user;
  }

  async login(email, password) {
    try {
      const response = await authClient.post("/auth/login", {
        email,
        password,
      });

      this._storeAuthData(response.data);
      return response.data;
    } catch (error) {
      throw createAuthError(error, "Login failed", true);
    }
  }

  async register(email, password) {
    try {
      const response = await authClient.post("/auth/register", {
        email,
        password,
      });

      this._storeAuthData(response.data);
      return response.data;
    } catch (error) {
      throw createAuthError(error, "Registration failed", true);
    }
  }

  /**
   * Clears all local auth state: removes user from localStorage, flushes the
   * inflight request cache, and resets the in-memory user cache.
   * Tokens live in httpOnly cookies — the browser clears them when the server
   * calls clearAuthCookies() (e.g. on logout or session expiry).
   */
  clearLocalAuth() {
    localStorage.removeItem(STORAGE_KEYS.USER);
    this.user = null;
    // Clear API request cache to prevent stale entries with old auth fingerprint
    clearInflightCache();
  }

  /**
   * Logout user - invalidates refresh token on server and clears local state.
   * The server clears the httpOnly cookies; this method clears localStorage.
   * @returns {Promise<void>}
   */
  async logout() {
    try {
      // withCredentials on authClient sends the access_token cookie so the
      // backend can authenticate the request and null out the refresh token.
      await authClient.post("/auth/logout");
    } catch (error) {
      // Ignore errors — always clear local state even if the server call fails.
      if (process.env.NODE_ENV === "development") {
        console.warn("Server logout failed:", error.message);
      }
    }

    this.clearLocalAuth();
  }

  getCurrentUser() {
    if (!this.user) {
      const userStr = localStorage.getItem(STORAGE_KEYS.USER);
      if (userStr) {
        try {
          this.user = JSON.parse(userStr);
        } catch {
          return null;
        }
      }
    }
    return this.user;
  }

  /**
   * Returns true when local session data (user object) is present in
   * localStorage.  This is a LOCAL STATE CHECK, not a live session check.
   *
   * What it does NOT check: whether the httpOnly auth cookies are still
   * valid.  Tokens live in cookies managed entirely by the browser — JS
   * cannot read them — so expiry can only be confirmed by making a request.
   * When the cookies have expired the next API call will receive a 401,
   * the interceptor in api.js will attempt a silent refresh, and if that
   * also fails it clears localStorage and redirects to login.
   *
   * Implication for callers: a true result means "the user logged in and
   * has not explicitly logged out on this device".  It does not guarantee
   * the session is still valid on the server.
   *
   * Pure predicate — never modifies state, safe to call during React render.
   */
  isAuthenticated() {
    return !!this.getCurrentUser();
  }

  /**
   * Verify the current session against the server.
   *
   * Always resolves to a boolean — never rejects, never returns null.
   *   true  — server confirmed the session is valid
   *   false — session is invalid, expired, or a network/server error occurred
   *           (a network error is indistinguishable from an expired session
   *           from the client's perspective, so both are treated as "not verified")
   *
   * Side effect: clears local auth state on 401/403 to prevent redirect loops.
   *
   * @returns {Promise<boolean>}
   */
  async verifyToken() {
    // Short-circuit: if there is no local user object the session is already
    // known to be gone — skip the network call.  (The previous token-based
    // implementation had the equivalent guard: `if (!token) return false`.)
    if (!this.isAuthenticated()) return false;

    try {
      // Cookies are sent automatically — no Authorization header needed.
      const response = await authClient.get("/auth/verify");
      return response.data?.valid ?? false;
    } catch (error) {
      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        if (process.env.NODE_ENV === "development") {
          console.warn("Network error during token verification");
        }
        // Return false, not null. A network error means we cannot confirm the
        // session is valid; treat it the same as "not verified" so callers
        // always receive a boolean and can use strict equality checks.
        return false;
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        this.clearLocalAuth();
        return false;
      }

      if (process.env.NODE_ENV === "development") {
        console.error("Token verification error:", error.message);
      }
      return false;
    }
  }

  // ============================================================================
  // Generic POST helper
  // ============================================================================

  /**
   * POST to an auth endpoint and return the unwrapped response data,
   * or throw a structured AuthError on failure.  All five password/verification
   * methods share this shape — they differ only in endpoint, body, and message.
   *
   * Using createAuthError consistently means callers always get `.isNetworkError`
   * and `.isCircuitBreakerError` on thrown errors.
   *
   * @param {string} endpoint
   * @param {Object} data - Request body
   * @param {string} defaultMessage - Fallback error message
   * @param {import('axios').AxiosRequestConfig} [axiosConfig] - Optional per-call axios config
   *   (e.g. signal for AbortController, custom headers, timeout override).
   * @private
   */
  async _post(endpoint, data, defaultMessage, axiosConfig) {
    try {
      const response = await authClient.post(endpoint, data, axiosConfig);
      return response.data;
    } catch (error) {
      throw createAuthError(error, defaultMessage);
    }
  }

  // ============================================================================
  // Password Reset Methods
  // ============================================================================

  async forgotPassword(email) {
    return this._post(
      "/auth/forgot-password",
      { email },
      "Failed to send reset email",
    );
  }

  async resetPassword(token, password) {
    return this._post(
      "/auth/reset-password",
      { token, password },
      "Failed to reset password",
    );
  }

  // ============================================================================
  // Email Verification Methods
  // ============================================================================

  async verifyEmail(token) {
    return this._post(
      "/auth/verify-email",
      { token },
      "Failed to verify email",
    );
  }

  /**
   * Resend email verification (requires authentication via access_token cookie).
   */
  async resendVerification() {
    return this._post(
      "/auth/resend-verification",
      {},
      "Failed to resend verification",
    );
  }

  // ============================================================================
  // Password Change Method (for authenticated users)
  // ============================================================================

  /**
   * Change password for authenticated user (requires access_token cookie).
   */
  async changePassword(currentPassword, newPassword) {
    return this._post(
      "/auth/change-password",
      { currentPassword, newPassword },
      "Failed to change password",
    );
  }
}

const authService = new AuthService();
export default authService;

/**
 * Pure predicate — returns true when the given user object has the admin role.
 * Centralises role checking so every admin gate reads from one place.
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function isAdminUser(user) {
  return user?.role === "admin";
}
